/**
 * Subscription Types and Constants
 * 
 * Defines subscription statuses, types, and related constants
 * for the ArbiWin subscription management system.
 */

/**
 * Subscription Status Types
 */
export const SUBSCRIPTION_STATUS = {
  // No subscription exists
  NONE: 'none',
  
  // Subscription exists but not activated
  INACTIVE: 'inactive',
  
  // Subscription is active and valid
  ACTIVE: 'active',
  
  // Subscription has expired
  EXPIRED: 'expired',
  
  // Subscription is suspended
  SUSPENDED: 'suspended',
  
  // Subscription is cancelled
  CANCELLED: 'cancelled',
  
  // Loading state while checking status
  LOADING: 'loading',
  
  // Error state
  ERROR: 'error'
};

/**
 * Subscription Types
 */
export const SUBSCRIPTION_TYPES = {
  // Basic subscription
  BASIC: 'basic',
  
  // Premium subscription
  PREMIUM: 'premium',
  
  // Enterprise subscription
  ENTERPRISE: 'enterprise'
};

/**
 * Subscription Features
 */
export const SUBSCRIPTION_FEATURES = {
  // Access to surebet data
  SUREBET_ACCESS: 'surebet_access',
  
  // Access to calculator
  CALCULATOR_ACCESS: 'calculator_access',
  
  // Access to filters
  FILTER_ACCESS: 'filter_access',
  
  // Real-time data updates
  REALTIME_UPDATES: 'realtime_updates',
  
  // Historical data access
  HISTORICAL_DATA: 'historical_data',
  
  // Advanced analytics
  ADVANCED_ANALYTICS: 'advanced_analytics',
  
  // Priority support
  PRIORITY_SUPPORT: 'priority_support'
};

/**
 * Subscription Error Types
 */
export const SUBSCRIPTION_ERROR_TYPES = {
  // Network error
  NETWORK_ERROR: 'network_error',
  
  // Server error
  SERVER_ERROR: 'server_error',
  
  // Validation error
  VALIDATION_ERROR: 'validation_error',
  
  // Authentication error
  AUTH_ERROR: 'auth_error',
  
  // Payment required error
  PAYMENT_REQUIRED: 'payment_required',
  
  // Subscription not found
  NOT_FOUND: 'not_found',
  
  // Subscription already active
  ALREADY_ACTIVE: 'already_active',
  
  // Subscription activation failed
  ACTIVATION_FAILED: 'activation_failed',
  
  // Unknown error
  UNKNOWN: 'unknown'
};

/**
 * Subscription Action Types
 */
export const SUBSCRIPTION_ACTIONS = {
  // Check subscription status
  CHECK_STATUS: 'CHECK_STATUS',
  CHECK_STATUS_SUCCESS: 'CHECK_STATUS_SUCCESS',
  CHECK_STATUS_FAILURE: 'CHECK_STATUS_FAILURE',
  
  // Activate subscription
  ACTIVATE: 'ACTIVATE',
  ACTIVATE_SUCCESS: 'ACTIVATE_SUCCESS',
  ACTIVATE_FAILURE: 'ACTIVATE_FAILURE',
  
  // Deactivate subscription
  DEACTIVATE: 'DEACTIVATE',
  DEACTIVATE_SUCCESS: 'DEACTIVATE_SUCCESS',
  DEACTIVATE_FAILURE: 'DEACTIVATE_FAILURE',
  
  // Renew subscription
  RENEW: 'RENEW',
  RENEW_SUCCESS: 'RENEW_SUCCESS',
  RENEW_FAILURE: 'RENEW_FAILURE',
  
  // Cancel subscription
  CANCEL: 'CANCEL',
  CANCEL_SUCCESS: 'CANCEL_SUCCESS',
  CANCEL_FAILURE: 'CANCEL_FAILURE',
  
  // Refresh subscription data
  REFRESH: 'REFRESH',
  REFRESH_SUCCESS: 'REFRESH_SUCCESS',
  REFRESH_FAILURE: 'REFRESH_FAILURE',
  
  // Clear subscription data
  CLEAR: 'CLEAR',
  
  // Set loading state
  SET_LOADING: 'SET_LOADING',
  
  // Set error state
  SET_ERROR: 'SET_ERROR'
};

/**
 * Default subscription configuration
 */
export const DEFAULT_SUBSCRIPTION_CONFIG = {
  // Auto-refresh interval in milliseconds (10 minutes)
  AUTO_REFRESH_INTERVAL: 10 * 60 * 1000,
  
  // Retry attempts for failed requests
  MAX_RETRY_ATTEMPTS: 3,
  
  // Retry delay in milliseconds
  RETRY_DELAY: 1000,
  
  // Cache duration in milliseconds (5 minutes)
  CACHE_DURATION: 5 * 60 * 1000,
  
  // Features available for each subscription type
  FEATURES_BY_TYPE: {
    [SUBSCRIPTION_TYPES.BASIC]: [
      SUBSCRIPTION_FEATURES.SUREBET_ACCESS,
      SUBSCRIPTION_FEATURES.CALCULATOR_ACCESS,
      SUBSCRIPTION_FEATURES.FILTER_ACCESS
    ],
    [SUBSCRIPTION_TYPES.PREMIUM]: [
      SUBSCRIPTION_FEATURES.SUREBET_ACCESS,
      SUBSCRIPTION_FEATURES.CALCULATOR_ACCESS,
      SUBSCRIPTION_FEATURES.FILTER_ACCESS,
      SUBSCRIPTION_FEATURES.REALTIME_UPDATES,
      SUBSCRIPTION_FEATURES.HISTORICAL_DATA
    ],
    [SUBSCRIPTION_TYPES.ENTERPRISE]: [
      SUBSCRIPTION_FEATURES.SUREBET_ACCESS,
      SUBSCRIPTION_FEATURES.CALCULATOR_ACCESS,
      SUBSCRIPTION_FEATURES.FILTER_ACCESS,
      SUBSCRIPTION_FEATURES.REALTIME_UPDATES,
      SUBSCRIPTION_FEATURES.HISTORICAL_DATA,
      SUBSCRIPTION_FEATURES.ADVANCED_ANALYTICS,
      SUBSCRIPTION_FEATURES.PRIORITY_SUPPORT
    ]
  }
};

/**
 * Subscription status display labels
 */
export const SUBSCRIPTION_STATUS_LABELS = {
  [SUBSCRIPTION_STATUS.NONE]: {
    bg: 'Няма абонамент',
    en: 'No Subscription'
  },
  [SUBSCRIPTION_STATUS.INACTIVE]: {
    bg: 'Неактивен',
    en: 'Inactive'
  },
  [SUBSCRIPTION_STATUS.ACTIVE]: {
    bg: 'Активен',
    en: 'Active'
  },
  [SUBSCRIPTION_STATUS.EXPIRED]: {
    bg: 'Изтекъл',
    en: 'Expired'
  },
  [SUBSCRIPTION_STATUS.SUSPENDED]: {
    bg: 'Спрян',
    en: 'Suspended'
  },
  [SUBSCRIPTION_STATUS.CANCELLED]: {
    bg: 'Отменен',
    en: 'Cancelled'
  },
  [SUBSCRIPTION_STATUS.LOADING]: {
    bg: 'Зареждане...',
    en: 'Loading...'
  },
  [SUBSCRIPTION_STATUS.ERROR]: {
    bg: 'Грешка',
    en: 'Error'
  }
};

/**
 * Subscription type display labels
 */
export const SUBSCRIPTION_TYPE_LABELS = {
  [SUBSCRIPTION_TYPES.BASIC]: {
    bg: 'Базов',
    en: 'Basic'
  },
  [SUBSCRIPTION_TYPES.PREMIUM]: {
    bg: 'Премиум',
    en: 'Premium'
  },
  [SUBSCRIPTION_TYPES.ENTERPRISE]: {
    bg: 'Корпоративен',
    en: 'Enterprise'
  }
};
